<script setup>
import { useI18n } from 'vue-i18n'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { useTweaks, PALETTES, FONT_PAIRS, BG_TINTS, SIZES, BUTTON_RADII, INPUT_RADII, TABLE_RADII, VALUE_WEIGHTS } from '@/composables/useTweaks'

const { t } = useI18n()
const { isMobile } = useBreakpoint()
const { tweaks, setTweak } = useTweaks()

const paletteOpts  = Object.entries(PALETTES).map(([v, p]) => ({ value: v, label: p.name }))
const modeOpts     = [{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]
const fontOpts     = Object.entries(FONT_PAIRS).map(([v, f]) => ({ value: v, label: f.label, display: f.display, body: f.body }))
const bgTintOpts   = Object.entries(BG_TINTS).map(([v, t]) => ({
  value: v,
  label: t.label,
  swatchLight: v === 'auto' ? t.swatch.light : t.light.bg,
  swatchDark:  v === 'auto' ? t.swatch.dark  : t.dark.bg,
}))
const sizeOpts      = Object.entries(SIZES).map(([v, s]) => ({ value: v, label: s.label }))
const btnRadiusOpts = Object.entries(BUTTON_RADII).map(([v, r]) => ({ value: v, label: r.label, preview: r.value }))
const inpRadiusOpts = Object.entries(INPUT_RADII).map(([v, r]) => ({ value: v, label: r.label, preview: r.value }))
const tblRadiusOpts = Object.entries(TABLE_RADII).map(([v, r]) => ({ value: v, label: r.label, preview: r.value }))
const valueWeightOpts = Object.entries(VALUE_WEIGHTS).map(([v, w]) => ({ value: v, label: w.label, weight: w.value }))
</script>

<template>
  <div
    v-if="isMobile"
    class="rounded-[var(--radius-button)] border border-[color:var(--line)] bg-[color:var(--surfaceAlt)] p-6 text-center"
  >
    <p class="text-sm text-[color:var(--inkSoft)]">
      {{ t('settings.appearance.mobileBlocked') }}
    </p>
  </div>
  <div
    v-else
    class="flex flex-col gap-4"
  >
    <div class="tweak-row">
      <span class="tweak-label">Palette</span>
      <div class="seg">
        <button
          v-for="o in paletteOpts"
          :key="o.value"
          type="button"
          :class="{ active: tweaks.palette === o.value }"
          @click="setTweak('palette', o.value)"
        >
          {{ o.label }}
        </button>
      </div>
    </div>

    <div class="tweak-row">
      <span class="tweak-label">Theme</span>
      <div class="seg">
        <button
          v-for="o in modeOpts"
          :key="o.value"
          type="button"
          :class="{ active: tweaks.mode === o.value }"
          @click="setTweak('mode', o.value)"
        >
          {{ o.label }}
        </button>
      </div>
    </div>

    <div class="tweak-row">
      <span class="tweak-label">Background</span>
      <div class="bg-swatches">
        <button
          v-for="o in bgTintOpts"
          :key="o.value"
          type="button"
          class="bg-swatch"
          :class="{ active: tweaks.bgTint === o.value }"
          :title="o.label"
          :aria-label="o.label"
          @click="setTweak('bgTint', o.value)"
        >
          <span
            class="chip"
            :style="{ background: tweaks.mode === 'dark' ? o.swatchDark : o.swatchLight }"
          />
          <span class="lbl">{{ o.label }}</span>
        </button>
      </div>
    </div>

    <div class="tweak-row">
      <span class="tweak-label">Typography</span>
      <div class="grid grid-cols-2 gap-1.5">
        <button
          v-for="o in fontOpts"
          :key="o.value"
          type="button"
          class="font-swatch"
          :class="{ active: tweaks.fontPair === o.value }"
          @click="setTweak('fontPair', o.value)"
        >
          <span :style="{ fontFamily: o.display, fontSize: '18px', color: 'var(--ink)', lineHeight: 1.1 }">Aa</span>
          <span
            class="text-[0.625rem] uppercase tracking-[0.08em]"
            :style="{
              fontFamily: o.body,
              color: tweaks.fontPair === o.value ? 'var(--accent)' : 'var(--muted)',
            }"
          >{{ o.label }}</span>
        </button>
      </div>
    </div>

    <div class="tweak-row">
      <span class="tweak-label">Size</span>
      <div class="seg">
        <button
          v-for="o in sizeOpts"
          :key="o.value"
          type="button"
          :class="{ active: tweaks.fontSize === o.value }"
          @click="setTweak('fontSize', o.value)"
        >
          {{ o.label }}
        </button>
      </div>
    </div>

    <div class="tweak-row">
      <span class="tweak-label">Button shape</span>
      <div class="shape-row">
        <button
          v-for="o in btnRadiusOpts"
          :key="o.value"
          type="button"
          class="shape-chip"
          :class="{ active: tweaks.buttonRadius === o.value }"
          :style="{ borderRadius: o.preview }"
          @click="setTweak('buttonRadius', o.value)"
        >
          {{ o.label }}
        </button>
      </div>
    </div>

    <div class="tweak-row">
      <span class="tweak-label">Input shape</span>
      <div class="shape-row">
        <button
          v-for="o in inpRadiusOpts"
          :key="o.value"
          type="button"
          class="shape-chip"
          :class="{ active: tweaks.inputRadius === o.value }"
          :style="{ borderRadius: o.preview }"
          @click="setTweak('inputRadius', o.value)"
        >
          {{ o.label }}
        </button>
      </div>
    </div>

    <div class="tweak-row">
      <span class="tweak-label">Table corners</span>
      <div class="shape-row">
        <button
          v-for="o in tblRadiusOpts"
          :key="o.value"
          type="button"
          class="shape-chip"
          :class="{ active: tweaks.tableRadius === o.value }"
          :style="{ borderRadius: o.preview }"
          @click="setTweak('tableRadius', o.value)"
        >
          {{ o.label }}
        </button>
      </div>
    </div>

    <div class="tweak-row">
      <span class="tweak-label">Value weight</span>
      <div class="shape-row">
        <button
          v-for="o in valueWeightOpts"
          :key="o.value"
          type="button"
          class="shape-chip"
          :class="{ active: tweaks.valueWeight === o.value }"
          :style="{ fontWeight: o.weight }"
          @click="setTweak('valueWeight', o.value)"
        >
          {{ o.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tweak-row { display: flex; flex-direction: column; gap: 6px; }
.tweak-label {
  font-family: var(--mono);
  font-size: 0.625rem;
  color: var(--muted);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.seg {
  display: inline-flex;
  background: var(--surfaceAlt);
  border-radius: 999px;
  padding: 3px;
  gap: 2px;
  flex-wrap: wrap;
  border: 1px solid var(--line);
  align-self: flex-start;
}
.seg button {
  padding: 6px 12px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  background: transparent;
  color: var(--muted);
  font-size: 0.75rem;
  font-family: inherit;
  font-weight: 400;
  transition: background 120ms, color 120ms;
}
.seg button.active {
  background: var(--surface);
  color: var(--ink);
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}
.font-swatch {
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  border: 1px solid var(--line);
  background: var(--surface);
  display: flex;
  flex-direction: column;
  gap: 2px;
  transition: border-color 120ms, background 120ms;
}
.font-swatch.active {
  border-color: var(--accent);
  background: var(--accentSoft);
}
.bg-swatches {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.bg-swatch {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 6px 4px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--surface);
  cursor: pointer;
  transition: border-color 120ms, background 120ms;
}
.bg-swatch .chip {
  width: 100%; height: 24px; border-radius: 6px;
  border: 1px solid var(--line);
}
.bg-swatch .lbl {
  font-size: 0.625rem; color: var(--muted);
  font-family: var(--mono); letter-spacing: 0.05em;
}
.bg-swatch.active { border-color: var(--accent); }
.bg-swatch.active .lbl { color: var(--accent); }

.shape-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.shape-chip {
  padding: 8px 10px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.75rem;
  cursor: pointer;
  transition: border-color 120ms, background 120ms, color 120ms;
}
.shape-chip:hover { background: var(--surfaceAlt); }
.shape-chip.active {
  border-color: var(--accent);
  background: var(--accentSoft);
  color: var(--accent);
  font-weight: 500;
}
</style>
